import { forwardRef } from 'react';
import type { BuildState, BuildDerived } from '@/types';
import { ArrowGlyph, RENDER_VIEW, DEFAULT_APPEARANCE, type Appearance } from './RealisticArrow';
import { iconicKillRanges } from './KillVerdict';

interface ShareCardProps {
  state: BuildState;
  derived: BuildDerived;
  finish?: Appearance;
}

const W = 1080;
const H = 1080;
const FONT = 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * Shareable build card — a square (1080×1080) social-ready image of the user's
 * arrow: the true-scale render + headline stats + branding + the forge URL.
 * Rendered as a self-contained SVG (all colors inline, gradients in defs) so it
 * rasterizes cleanly to PNG via canvas. Every card carries the URL back to the
 * builder — the share loop that feeds the funnel.
 *
 * Exposes its <svg> via ref so the export helper can serialize it.
 */
export const ShareCard = forwardRef<SVGSVGElement, ShareCardProps>(function ShareCard(
  { state, derived, finish = DEFAULT_APPEARANCE },
  ref,
) {
  const name = state.name?.trim() || 'Custom Build';
  const stats: { value: string; label: string }[] = [
    { value: `${state.length.toFixed(1)}"`, label: 'LENGTH' },
    { value: `${Math.round(derived.weight)}`, label: 'GRAINS' },
    { value: `${derived.foc.toFixed(1)}%`, label: 'FOC' },
    { value: `${derived.balancePoint.toFixed(1)}"`, label: 'BALANCE' },
  ];
  // Embed geometry: keep the arrow's native aspect (800×156).
  const arrowW = 1000;
  const arrowH = (arrowW * RENDER_VIEW.height) / RENDER_VIEW.width;
  const arrowX = (W - arrowW) / 2;
  const arrowY = 392;
  const colW = (W - 160) / stats.length;
  // Kill-verdict band — the screenshot hook.
  const kills = iconicKillRanges(state.fps, derived.weight);
  const colW3 = (W - 160) / 3;
  const shortName = (n: string) => (n === 'Whitetail deer' ? 'DEER' : n === 'Elk' ? 'ELK' : 'MOOSE');

  return (
    <svg ref={ref} viewBox={`0 0 ${W} ${H}`} width={W} height={H} className="w-full h-auto rounded-lg" xmlns="http://www.w3.org/2000/svg" role="img" aria-label={`Share card for ${name}`}>
      <defs>
        <linearGradient id="sc-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#111113" />
          <stop offset="55%" stopColor="#0a0a0b" />
          <stop offset="100%" stopColor="#050506" />
        </linearGradient>
        <radialGradient id="sc-glow" cx="0.5" cy="0.0" r="0.9">
          <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.16" />
          <stop offset="60%" stopColor="#f59e0b" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Background */}
      <rect x={0} y={0} width={W} height={H} fill="url(#sc-bg)" />
      <rect x={0} y={0} width={W} height={H} fill="url(#sc-glow)" />
      <rect x={0} y={0} width={W} height={H} fill="none" stroke="#27272a" strokeWidth={2} />
      <rect x={0} y={0} width={W} height={6} fill="#f59e0b" />

      {/* Header */}
      <text x={80} y={130} fontFamily={FONT} fontSize={40} fontWeight={800} letterSpacing={4} fill="#f59e0b">
        SPARROW FORGE
      </text>
      <text x={80} y={186} fontFamily={FONT} fontSize={52} fontWeight={700} fill="#fafafa">
        {clip(name, 28)}
      </text>
      <text x={80} y={232} fontFamily={MONO} fontSize={24} fill="#71717a">
        Tuned, not guessed.
      </text>

      {/* Arrow render (embedded glyph at native aspect) */}
      <svg x={arrowX} y={arrowY} width={arrowW} height={arrowH} viewBox={`0 0 ${RENDER_VIEW.width} ${RENDER_VIEW.height}`}>
        <ArrowGlyph state={state} finish={finish} />
      </svg>

      {/* Kill-verdict band — the screenshot hook */}
      <line x1={80} y1={620} x2={W - 80} y2={620} stroke="#27272a" strokeWidth={2} />
      <text x={W / 2} y={654} textAnchor="middle" fontFamily={MONO} fontSize={20} letterSpacing={3} fill="#71717a">
        ETHICALLY LETHAL TO
      </text>
      {kills.map((k, i) => {
        const cx = 80 + colW3 * (i + 0.5);
        const pass = k.ethicalTo > 0;
        const color = pass ? '#22c55e' : '#ef4444';
        const rangeTxt = k.ethicalTo >= 100 ? '100+ yd' : pass ? `${k.ethicalTo} yd` : 'no';
        return (
          <g key={k.name}>
            <text x={cx} y={702} textAnchor="middle" fontFamily={FONT} fontSize={34} fontWeight={700} fill={color}>
              {pass ? '✓' : '✕'} {shortName(k.name)}
            </text>
            <text x={cx} y={738} textAnchor="middle" fontFamily={FONT} fontSize={28} fontWeight={600} fill={color}>
              {rangeTxt}
            </text>
          </g>
        );
      })}

      {/* Stat strip */}
      <line x1={80} y1={760} x2={W - 80} y2={760} stroke="#27272a" strokeWidth={2} />
      {stats.map((s, i) => {
        const cx = 80 + colW * (i + 0.5);
        return (
          <g key={s.label}>
            <text x={cx} y={862} textAnchor="middle" fontFamily={FONT} fontSize={68} fontWeight={700} fill="#fafafa">
              {s.value}
            </text>
            <text x={cx} y={904} textAnchor="middle" fontFamily={MONO} fontSize={22} letterSpacing={2} fill="#71717a">
              {s.label}
            </text>
          </g>
        );
      })}
      <line x1={80} y1={948} x2={W - 80} y2={948} stroke="#27272a" strokeWidth={2} />

      {/* Footer / CTA */}
      <text x={80} y={1016} fontFamily={MONO} fontSize={28} fill="#a1a1aa">
        forge.sparrowexpeditions.com
      </text>
      <text x={W - 80} y={1016} textAnchor="end" fontFamily={FONT} fontSize={28} fontWeight={600} fill="#f59e0b">
        Build yours free →
      </text>
    </svg>
  );
});

function clip(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
